import type { ClientPortalFeatures } from "@cliently/schemas";

export type { ClientPortalFeatures } from "@cliently/schemas";

/** Suivi métriques numériques activé avec au moins une définition. */
export const resolveNumericTrackingConfigured = (
  features: ClientPortalFeatures | null | undefined,
): boolean => {
  return (
    features?.numericTrackingEnabled === true &&
    (features.numericDefinitions?.length ?? 0) > 0
  );
};

/**
 * Résolution « liaison vivante » des métriques visibles pour un client.
 *
 * Combine :
 *  - les `metricIds` directement assignés au client (ancien mécanisme via `metaData.private.numericMetricDefinitionIds`),
 *  - l'union des `metricIds` des groupes de métriques actuellement appliqués au client.
 *
 * Sémantique :
 *  - Tant qu'AUCUN id direct n'est défini ET qu'aucun groupe n'est assigné, `configured = false`
 *    et `ids` est vide : le client ne voit **aucune** métrique jusqu'à ce qu'un id direct ou un
 *    groupe lui soit explicitement assigné.
 *  - Sinon `configured = true` et `ids` est l'union dédupliquée et restreinte au catalogue d'origine.
 *
 * Les appelants doivent toujours filtrer sur `ids` (jamais retomber sur le catalogue complet
 * de l'organisation lorsque `configured` est `false`), afin qu'un client sans assignation n'ait
 * accès à aucune métrique.
 *
 * `directConfigured` indique si la clé `numericMetricDefinitionIds` était présente sur le client
 * (même vide). Ce cas est désormais équivalent à « pas configuré » côté visibilité (aucune métrique),
 * mais reste distingué pour l'UI de gestion (savoir si une assignation directe a déjà été faite).
 */
export const resolveEffectiveAssignedMetricIds = (params: {
  directIds: string[];
  directConfigured: boolean;
  groupMetricIds: string[][];
}): { configured: boolean; ids: string[] } => {
  const { directIds, directConfigured, groupMetricIds } = params;
  const hasGroups = groupMetricIds.some((ids) => ids.length > 0);
  const configured = directConfigured || hasGroups;
  if (!configured) {
    return { configured: false, ids: [] };
  }
  const set = new Set<string>();
  for (const id of directIds) {
    if (typeof id === "string" && id.length > 0) set.add(id);
  }
  for (const arr of groupMetricIds) {
    for (const id of arr) {
      if (typeof id === "string" && id.length > 0) set.add(id);
    }
  }
  return { configured: true, ids: Array.from(set) };
};
